import React, { useState } from "react";
import styled from "styled-components";
import { FaFileAlt, FaCodeBranch } from "react-icons/fa";
import { MdGroup } from "react-icons/md";

function FigmaPreview({ previewUrl }) {
  const [previewFailed, setPreviewFailed] = useState(false);
  if (!previewUrl) return null;

  return (
    <Section>
      <Label>Figma Preview</Label>
      {previewFailed ? (
        <FallbackBox>
          ⚠️ Failed to load Figma preview.
          <br />
          <a href={previewUrl} target="_blank" rel="noopener noreferrer">
            Open PDF in new tab
          </a>
        </FallbackBox>
      ) : (
        <>
          <StyledIframe
            src={previewUrl}
            title="Figma Preview"
            width="100%"
            height="600"
            allow="fullscreen"         
            allowFullScreen              
            onError={() => setPreviewFailed(true)}
          />
        </>
      )}
    </Section>
  );
}

const FigmaPopupCard = ({ link, meta, groupMembers, onClose }) => {
  if (!meta || Object.keys(meta).length === 0) return null;

  const editorEntries = Object.entries(meta.editors || {});
  const previewUrl = meta.pdfStorage?.url;

  return (
    <ContentWrapper>
      <CloseButton onClick={onClose}>✕</CloseButton>
      <Card>
        <TitleRow>
          <Label>Figma File</Label>
        </TitleRow>
        <Header>{meta.fileName}</Header>

        <Section>
          <Label>Figma Metadata</Label>
          <MetaGrid>

            {/* Edit Count */}
            <MetaBox
              color={{
                bg: "#fef2f2",
                border: "#fecaca",
                text: "#dc2626",
                value: "#b91c1c",
              }}
            >
              <IconWrapper>
                <FaCodeBranch size={24} />
              </IconWrapper>
              <MetaText color={{ text: "#dc2626", value: "#b91c1c" }}>
                <p>Edit Count</p>
                <p>
                  {meta.versionChange?.count ?? 0}
                </p>
              </MetaText>
            </MetaBox>


            {/* Version Status */}
            <MetaBox
              color={{
                bg: "#faf5ff",
                border: "#e9d5ff",
                text: "#7e22ce",
                value: "#6b21a8",
              }}
            >
              <IconWrapper>
                <FaCodeBranch size={24} />
              </IconWrapper>
              <MetaText color={{ text: "#7e22ce", value: "#6b21a8" }}>
                <p>Version Status</p>
                <p>
                  {meta.versionChange?.status === "initial"
                    ? "First submission"
                    : meta.versionChange?.status === "no_change"
                      ? "Same"
                      : meta.versionChange?.status === "updated"
                        ? "New version"
                        : "Unknown"}
                </p>
              </MetaText>
            </MetaBox>

            {/* File Type */}
            <MetaBox
              color={{
                bg: "#ecfdf5",
                border: "#bbf7d0",
                text: "#10b981",
                value: "#047857",
              }}
            >
              <IconWrapper>
                <FaFileAlt size={24} />
              </IconWrapper>
              <MetaText color={{ text: "#10b981", value: "#047857" }}>
                <p>File Type</p>
                <p>
                  {meta.fileType
                    ? meta.fileType.charAt(0).toUpperCase() +
                    meta.fileType.slice(1)
                    : "Unknown"}
                </p>
              </MetaText>
            </MetaBox>

            {/* Collaborators */}
            {editorEntries.length > 0 && (
              <MetaBox
                full
                color={{
                  bg: "#fefce8",
                  border: "#fef08a",
                  text: "#eab308",
                  value: "#a16207",
                }}
              >
                <IconWrapper>
                  <MdGroup size={24} />
                </IconWrapper>
                <MetaText color={{ text: "#facc15", value: "#ca8a04" }}>
                  <p>
                    Figma Collaborator
                    {editorEntries.length !== 1 ? "s" : ""}
                  </p>
                  <CollaboratorList>
                    {editorEntries.map(([name, count], i) => {
                      const member = groupMembers?.find(
                        (m) =>
                          m.figma?.handle?.toLowerCase?.() ===
                          name.toLowerCase() ||
                          m.name?.toLowerCase?.() === name.toLowerCase() ||
                          m.email?.toLowerCase?.() === name.toLowerCase()
                      );
                      const isUnauthorized = !member;
                      return (
                        <ContributorRow key={i}>
                          <span
                            style={{
                              color: isUnauthorized ? "#dc2626" : "inherit",
                            }}
                          >
                            {member
                              ? `${member.name} (${count} edits)`
                              : `Unauthorized User (${name} – ${count} edits)`}
                          </span>
                        </ContributorRow>
                      );
                    })}
                  </CollaboratorList>
                </MetaText>
              </MetaBox>
            )}
          </MetaGrid>
        </Section>

        {previewUrl && <FigmaPreview previewUrl={previewUrl} />}
        <ButtonSection>
          {meta.pdfStorage?.url && (
            <ViewButton href={meta.pdfStorage.url} target="_blank" rel="noopener noreferrer">
              View PDF
            </ViewButton>
          )}
          {link && (
            <ViewButton href={link} target="_blank" rel="noopener noreferrer">
              Go to Figma
            </ViewButton>
          )}
        </ButtonSection>
      </Card>
    </ContentWrapper>
  );
};

export default FigmaPopupCard;

/* ---------- STYLES (mostly reused from GoogleDocPopupCard) ---------- */
const ContentWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding-top: 15px;
  padding-bottom: 15px;
  align-items: flex-start;
`;

const CloseButton = styled.button`
  position: absolute;
  top: -8px;
  right: 0;
  background: transparent;
  border: none;
  font-size: 1.8rem;
  font-weight: 700;
  color: #6b7280;
  cursor: pointer;
  &:hover {
    color: #111827;
  }
`;

const Card = styled.div`
  background: white;
  width: 90%;
  padding: 2rem 2rem 1rem 2rem;
  border-radius: 0.75rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  margin: 2rem auto;
  text-align: left;
  border: 1px solid #e5e7eb;
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
`;

const Header = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const Label = styled.small`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Section = styled.div`
  margin-bottom: 1.5rem;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const MetaBox = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "full",
})`
  display: flex;
  align-items: center;
  background: ${({ color }) => color.bg};
  border: 1px solid ${({ color }) => color.border};
  padding: 1rem;
  border-radius: 0.5rem;
  grid-column: ${({ full }) => (full ? "span 2" : "auto")};
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
`;

const MetaText = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "color" && prop !== "small",
})`
  p:first-child {
    font-size: 0.875rem;
    color: ${({ color }) => color.text};
    font-weight: 500;
  }

  p:last-child {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${({ color }) => color.value};
  }
`;

const ContributorRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0.4rem;
`;

const CollaboratorList = styled.div`
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  font-size: 1rem;
  font-weight: 500;
  color: #1f2937;
`;

const FallbackBox = styled.div`
  padding: 1rem;
  background-color: #fef2f2;
  color: #991b1b;
  border: 1px solid #fca5a5;
  border-radius: 0.5rem;
  text-align: center;
  font-size: 0.95rem;
  a {
    color: #2563eb;
    text-decoration: underline;
  }
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 600px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
`;

const ButtonSection = styled.div`
  display: flex;
  gap: 1rem;
  margin: 1.5rem 0 1.2rem 0;
  flex-wrap: wrap;
  justify-content: center;
`;

const ViewButton = styled.a`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background-color: #0b99ff;
  color: white;
  text-decoration: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  transition: background-color 0.2s ease;
  cursor: pointer;

  &:hover {
    background-color: #0a7fd9;
    scale: 1.02;
  }

  &:active {
    background-color: #0966b3;
  }
`;
